/* eslint-disable max-len */
import * as React from 'react';
import {
  Box, Button, List, ListItem, ListItemText, DialogTitle, DialogContent,
  DialogActions, Dialog, RadioGroup, Radio, FormControlLabel,
  ListItemAvatar, Avatar, Typography, Chip, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { GeoPoint } from 'firebase/firestore';
import haversine from 'haversine';

import CategoryDriver, { CategoryDriverText } from '../../../models/enums/categoryDriver';
import { phoneNumber } from '../../../components/mask';
import driverService from '../../../services/driverService';
import AlertDialog from '../../../components/alertDialog';
import callService from '../../../services/callService';
import { useAuth } from '../../../contexts/auth';

interface Driver {
  id: string;
  name: string;
  phone: string;
  photo: string;
  category: CategoryDriver;
  distance: number
}

interface DriverOnline extends Driver {
  isBusy: boolean;
  minutesAgo?: number;
}

const formatMinutesAgo = (minutesAgo?: number) => {
  if (minutesAgo == null || Number.isNaN(minutesAgo)) return null;
  if (minutesAgo < 1) return 'Atualizado agora';
  if (minutesAgo === 1) return 'há 1 min';
  if (minutesAgo < 60) return `há ${minutesAgo} min`;
  const hours = Math.floor(minutesAgo / 60);
  return hours === 1 ? 'há 1 h' : `há ${hours} h`;
};

type DriverSortBy = 'distance' | 'name';

interface OwnProps {
  callId: string;
  pickupLocation: GeoPoint | null;
  driver: Driver | null;
  subsidiaryId: string | null | undefined;
  status: string;
  isEnabled: false | undefined;
}

export interface ConfirmationDialogRawProps {
  id: string;
  keepMounted: boolean;
  value: string;
  open: boolean;
  drivers: DriverOnline[];
  // eslint-disable-next-line no-unused-vars
  onClose: (value?: string) => void;
}

function ConfirmationDialogRaw(props: ConfirmationDialogRawProps) {
  const {
    onClose, value: valueProp, open, drivers, ...other
  } = props;
  const [value, setValue] = React.useState(valueProp);
  const [sortBy, setSortBy] = React.useState<DriverSortBy>('distance');
  const radioGroupRef = React.useRef<HTMLElement>(null);

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  const handleCancel = () => { onClose(); };
  const handleOk = () => { onClose(value); };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
  };

  const handleSortBy = (_event: React.MouseEvent<HTMLElement>, nextSort: DriverSortBy | null) => {
    if (nextSort) setSortBy(nextSort);
  };

  if (!drivers.length) {
    return <AlertDialog open={open} handleClose={handleCancel} message="Nenhum condutor disponível no momento" />;
  }

  const sortedDrivers = [...drivers].sort((a, b) => {
    if (sortBy === 'name') {
      return (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' });
    }
    const byDistance = a.distance - b.distance;
    if (byDistance !== 0) return byDistance;
    return (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' });
  });

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '100%', maxHeight: 560 } }}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEntering: handleEntering }}
      open={open}
      {...other}
    >
      <DialogTitle>Condutores</DialogTitle>
      <DialogContent dividers>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap'
        }}
        >
          <Typography variant="body2" color="text.secondary">
            {sortBy === 'distance'
              ? 'Ordenado pela distância até a coleta'
              : 'Ordenado por nome'}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={sortBy}
            onChange={handleSortBy}
            aria-label="Ordenar condutores"
          >
            <ToggleButton value="distance">Distância</ToggleButton>
            <ToggleButton value="name">Nome</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <RadioGroup
          ref={radioGroupRef}
          aria-label="drivers"
          name="drivers"
          value={value}
          onChange={handleChange}
        >
          {sortedDrivers.map((item) => {
            const selected = value === item.id;
            const isCurrent = item.id === valueProp;
            const lastUpdateLabel = formatMinutesAgo(item.minutesAgo);

            return (
              <FormControlLabel
                key={item.id}
                value={item.id}
                control={<Radio color="primary" />}
                labelPlacement="start"
                label={(
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, pr: 1, width: '100%'
                  }}
                  >
                    <Avatar
                      alt={item.name}
                      src={item.photo || undefined}
                      sx={{ width: 48, height: 48, fontWeight: 600 }}
                    >
                      {(item.name || '?').trim().charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {CategoryDriverText[item.category]}
                        {item.phone ? ` · ${phoneNumber(item.phone)}` : ''}
                      </Typography>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap'
                      }}
                      >
                        <Chip size="small" label={`${item.distance} km`} variant="outlined" />
                        {lastUpdateLabel && (
                          <Chip size="small" label={lastUpdateLabel} variant="outlined" title="Última atualização" />
                        )}
                        {isCurrent && <Chip size="small" color="primary" label="Atual" />}
                        {item.isBusy && <Chip size="small" color="warning" label="Ocupado" />}
                      </Box>
                    </Box>
                  </Box>
                )}
                sx={{
                  mx: 0,
                  mb: 1,
                  px: 1.5,
                  py: 0.5,
                  width: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'action.selected' : 'background.paper',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    width: '100%',
                    mr: 1
                  }
                }}
              />
            );
          })}
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {drivers.length === 1 ? '1 condutor' : `${drivers.length} condutores`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button autoFocus onClick={handleCancel}>Fechar</Button>
          <Button onClick={handleOk} disabled={value === valueProp || !value} variant="contained">Encaminhar</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default function ConfirmationDialog(props: OwnProps) {
  const {
    callId, pickupLocation, driver, subsidiaryId, status, isEnabled
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<DriverOnline[]>([]);

  const handleClickListItem = () => {
    // driverService.getAllOnline(subsidiaryId).then((result: any[]) => {
    //   const endCoordinate = { longitude: pickupLocation?.longitude ?? 0, latitude: pickupLocation?.latitude ?? 0 };
    //   // eslint-disable-next-line no-shadow
    //   const options: DriverOnline[] = result.map((x) => ({
    //     category: x.category,
    //     id: x.id,
    //     name: x.name,
    //     phone: x.phoneNumber,
    //     photo: x.photoURL,
    //     isBusy: x.isBusy,
    //     // eslint-disable-next-line max-len, no-restricted-globals
    //     distance: isNaN(haversine({ longitude: x.location?.longitude ?? x.lon, latitude: x.location?.latitude ?? x.lat }, endCoordinate, { unit: 'meter' })) ? 0 : parseFloat((haversine({ longitude: x.location?.longitude ?? x.lon, latitude: x.location.latitude ?? x.lat }, endCoordinate, { unit: 'meter' }) * 0.001).toFixed(2))
    //   }));
    //   setOptions(options);
    //   setOpen(true);
    // });

    driverService.getOnlineFromRedis(
      subsidiaryId || null,
      user?.uid || null,
      pickupLocation?.latitude ?? 0,
      pickupLocation?.longitude ?? 0
    ).then((listFromRedis: any[]) => {
      const endCoordinate = { longitude: pickupLocation?.longitude ?? 0, latitude: pickupLocation?.latitude ?? 0 };
      // eslint-disable-next-line no-shadow
      const options: DriverOnline[] = listFromRedis.map((x) => ({
        category: x.driver.category,
        id: x.driver.id,
        name: x.driver.name,
        phone: x.driver.phone,
        photo: x.driver.photo,
        isBusy: x.currentCall?.id?.length > 0,
        minutesAgo: x.minutesAgo,
        // eslint-disable-next-line max-len, no-restricted-globals
        distance: isNaN(haversine({ longitude: x.position?.longitude ?? x.lon, latitude: x.position?.latitude ?? x.lat }, endCoordinate, { unit: 'meter' })) ? 0 : parseFloat((haversine({ longitude: x.position?.longitude ?? x.lon, latitude: x.position.latitude ?? x.lat }, endCoordinate, { unit: 'meter' }) * 0.001).toFixed(2))
      }));
      setOptions(options);
      setOpen(true);
    });
  };

  const handleClose = (newDriverId?: string) => {
    setOptions([]);
    setOpen(false);
    if (newDriverId) {
      callService.changeDriverAsync(callId, newDriverId).then(() => {
        enqueueSnackbar('Chamada encaminhada com sucesso', { variant: 'success' });
      }).catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error' });
      });
    }
  };

  let title = isEnabled ? 'Aguardando...' : 'Sem informação';
  let subtitle = isEnabled ? 'Nenhum condutor aceitou até o momento' : '...';
  if (status === 'imported') {
    title = 'Aguardando o preparo do pedido';
    subtitle = '...';
  }
  const buttonEnabled = isEnabled && status !== 'imported';

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
      <List component="div" role="group">
        <ListItem
          alignItems="flex-start"
          button={buttonEnabled}
          divider
          aria-haspopup="true"
          aria-controls="drivers-picker"
          aria-label="condutor"
          onClick={buttonEnabled ? handleClickListItem : undefined}
        >
          <ListItemAvatar>
            <Avatar alt={driver?.name ?? 'Condutor'} src={driver?.photo || undefined}>
              {(driver?.name || '?').trim().charAt(0).toUpperCase()}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={driver?.name ?? title}
            secondary={(
              <>
                <Typography
                  sx={{ display: 'inline' }}
                  component="span"
                  variant="body2"
                  color="text.primary"
                >
                  {phoneNumber(driver?.phone)}
                </Typography>
                {driver?.category ? ` - ${CategoryDriverText[driver.category]}` : subtitle}
              </>
            )}
          />
        </ListItem>
        {open && (
          <ConfirmationDialogRaw
            id="drivers-picker"
            keepMounted
            drivers={options}
            open={open}
            onClose={handleClose}
            value={driver?.id ?? ''}
          />
        )}
      </List>
    </Box>
  );
}
